import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../../app/redux/hooks';
import Price from '../../../../common/components/price/Price';
import { appSubscriptionService } from '../../../../common/subscriptions/appSubscriptionService';
import { MenuItem } from '../../../api/menu';
import { OperatorLevel } from '../../../api/operators/Operator';
import { useIsAppTill, useIsSessionBlockNewTransactions, useOperatorSession } from '../../../appState';
import { useOfflineMode } from '../../../config';
import { showErrorDialog } from '../../../error-dialog/errorDialogSlice';
import {
  setQuantity,
  useActiveCategoryIds,
  useMenuItemCategories,
  useQuantity,
  useSelectHasLineItems,
  useStockLevels,
  useTransaction,
} from '../../orderSlice';

function MenuButton({ menuItem, color }: { menuItem?: MenuItem; color?: string }) {
  const dispatch = useAppDispatch();
  const categories = useMenuItemCategories();
  const activeCategoryIds = useActiveCategoryIds();
  const navigate = useNavigate();
  const isNewItemBlocked = useIsSessionBlockNewTransactions();
  const isAppTill = useIsAppTill();
  const hasLineItems = useSelectHasLineItems();
  const [isNavigating, setIsNavigating] = useState(false);
  const isOffline = useOfflineMode();
  const quantity = useQuantity();
  const transaction = useTransaction();
  const operatorSession = useOperatorSession();
  const stockLevels = useStockLevels();
  const pendingItemRef = useRef<MenuItem | null>(null);

  const proceedWithItem = useCallback(
    (menuItem: MenuItem) => {
      // Check if this is a game item
      if (isOffline === false && menuItem.isGame === true) {
        navigate(`./game/${menuItem.id}`);
        setTimeout(() => setIsNavigating(false), 1000);
        return;
      }

      // Check if item has no options - add directly without navigation
      const noOptions =
        menuItem.prices.length === 1 && menuItem.condiments.length === 0 && menuItem.addOns.length === 0 && menuItem.takeOffs.length === 0;

      const isWaste = transaction.some((ti) => ti.isWaste === true);

      if (noOptions && transaction.length > 0 && operatorSession && isWaste === false) {
        console.debug('Fast-path: Adding no-options item directly from MenuButton', menuItem.name);
        const transactionItem = {
          condiments: [],
          quantity: quantity,
          menuItemId: menuItem.id,
          unitOfSaleId: menuItem.prices[0].unitOfSaleId,
          transactionId: transaction[0].id,
          addOns: [],
          takeOffs: [],
          operatorId: operatorSession.operator.id,
          operatorLevel: operatorSession.operator.operatorLevel,
        };
        appSubscriptionService.addToBasketNext(transactionItem);
        dispatch(setQuantity(1));
        setTimeout(() => setIsNavigating(false), 300);
      } else {
        // Navigate to AddItem component for items with options
        navigate(`./add/${menuItem.id}`);
        setTimeout(() => setIsNavigating(false), 1000);
      }
    },
    [navigate, quantity, transaction, operatorSession, isOffline, dispatch],
  );

  useEffect(() => {
    const subscription = appSubscriptionService.errorButtonAlternateClickedObservable().subscribe((state) => {
      if (state?.type === 'stock-override' && state?.menuItem && pendingItemRef.current?.id === state.menuItem.id) {
        setIsNavigating(true);
        proceedWithItem(state.menuItem);
        pendingItemRef.current = null;
      }
    });

    return () => subscription.unsubscribe();
  }, [proceedWithItem]);

  const itemClick = (menuItem: MenuItem) => {
    const stockLevel = getStockLevel(menuItem.id);
    const isOutOfStock = stockLevel && stockLevel.currentLevel <= 0;
    const canOverrideStock =
      operatorSession &&
      (operatorSession.operator.operatorLevel === OperatorLevel.Admin ||
        operatorSession.operator.operatorLevel === OperatorLevel.Manager ||
        operatorSession.operator.operatorLevel === OperatorLevel.Supervisor);

    if (isAppTill || (isNewItemBlocked && hasLineItems === false) || isNavigating) return;

    // If out of stock, check if user can override
    if (isOutOfStock) {
      if (canOverrideStock) {
        // Store the pending item and show warning dialog for authorized users
        pendingItemRef.current = menuItem;
        dispatch(
          showErrorDialog({
            message: `${menuItem.name} has no stock available.<br /><br />Please confirm there is stock available to sell.`,
            dismissible: true,
            buttonText: 'Cancel',
            alternateButtonText: 'Confirm Stock Available',
            showAlternateButton: true,
            requireConfirmationOnClose: false,
            alternateState: {
              type: 'stock-override',
              menuItem: menuItem,
            },
          }),
        );
        return;
      } else {
        // Regular users cannot add out of stock items
        return;
      }
    }

    setIsNavigating(true);
    proceedWithItem(menuItem);
  };

  const getColour = (categoryIds: number[]) => {
    const childCategories = categories.filter((x) => categoryIds.includes(x.id) && x.parentCategoryId != undefined);
    if (childCategories.length > 0) return color ? color : `category-color-${activeCategoryIds.indexOf(childCategories[0].id) + 1}`;
    if (categoryIds.length > 0) return color ? color : `category-color-${activeCategoryIds.indexOf(categoryIds[0]) + 1}`;
  };

  const getCategoryName = (categoryIds: number[]) => {
    const childCategories = categories.filter((x) => categoryIds.includes(x.id) && x.parentCategoryId != undefined);
    if (childCategories.length > 0) return childCategories[0].name;
    if (categoryIds.length > 0) return categories.find((x) => x.id == categoryIds[0])?.name;
  };

  const getStockLevel = (menuItemId: number) => {
    return stockLevels.find((x) => x.menuItemId === menuItemId);
  };

  if (menuItem !== undefined) {
    if (menuItem && menuItem.prices.length > 0) {
      const stockLevel = getStockLevel(menuItem.id);
      const isOutOfStock = stockLevel && stockLevel.currentLevel <= 0;

      return (
        <div
          className={`pos-menu__item ${getColour(menuItem.menuItemCategoryIds)} ${
            isAppTill || (isNewItemBlocked && hasLineItems === false) || isNavigating || isOutOfStock ? 'menu-item-disabled' : ''
          } ${isOutOfStock ? 'out-of-stock' : ''}`}
          onClick={() => itemClick(menuItem)}
        >
          <p className="pos-menu__item-meta">{getCategoryName(menuItem.menuItemCategoryIds)}</p>
          <p className="pos-menu__category-text">{menuItem.name}</p>
          <p className="pos-menu__item-meta">
            <Price price={menuItem.currentPrice ? menuItem.currentPrice : 0} />
          </p>
        </div>
      );
    }
  }
  return <div className="pos-menu__item hidden-button"></div>;
}
export default MenuButton;
